import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

const hint = () => (navigator.language.split("-")[1] || "").toUpperCase();

export function useCurrency() {
  const { data } = useQuery({ queryKey: ["fx-geo"], queryFn: async () => (await api.get("/fx/geo", { params: { hint: hint() } })).data, staleTime: 3600e3 });
  const [cur, setCurState] = useState(localStorage.getItem("ccm_cur"));
  useEffect(() => { if (!cur && data) setCurState(data.currency); }, [data, cur]);
  const setCur = (c) => { localStorage.setItem("ccm_cur", c); setCurState(c); window.dispatchEvent(new Event("ccm-cur")); };
  useEffect(() => {
    const h = () => setCurState(localStorage.getItem("ccm_cur"));
    window.addEventListener("ccm-cur", h);
    return () => window.removeEventListener("ccm-cur", h);
  }, []);
  const currency = cur || "USD";
  const rate = data?.rates?.[currency] || 1;
  return { currency, rate, setCur, chargeable: data?.chargeable || ["USD"], fxDate: data?.fx_date, country: data?.country };
}

export const usd = (n) => "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
export const money = (n, c) => {
  try { return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: c, maximumFractionDigits: c === "USD" ? 0 : 2 }); }
  catch { return `${c} ${Number(n || 0).toFixed(2)}`; }
};

export function Price({ value, big, testId }) {
  const { currency, rate } = useCurrency();
  return (
    <span data-testid={testId}>
      <span className={`font-mono font-bold ${big ? "text-3xl" : ""}`}>{usd(value)} <span className={big ? "text-sm text-slate-400" : "text-[10px] text-slate-500"}>USD</span></span>
      {currency !== "USD" && <span className={`block font-mono ${big ? "text-sm" : "text-[11px]"} text-slate-400`}>≈ {money(value * rate, currency)}</span>}
    </span>
  );
}

export function CurrencySelect({ testId = "currency-select" }) {
  const { currency, setCur, chargeable, fxDate } = useCurrency();
  const opts = [...new Set([currency, ...chargeable])].sort();
  return (
    <div className="flex items-center justify-between gap-2 text-xs text-slate-400 mt-3">
      <span>Pay in</span>
      <select value={currency} onChange={(e) => setCur(e.target.value)} data-testid={testId} className="h-8 rounded-md bg-slate-900 border border-slate-700 px-2 text-sm text-white">
        {opts.map((c) => <option key={c}>{c}</option>)}
      </select>
      {fxDate && <span className="text-[10px] text-slate-500 truncate" title={fxDate}>FX {fxDate.slice(5, 16)}</span>}
    </div>
  );
}

const compact = (n, c) => {
  try { return Number(n).toLocaleString("en-US", { style: "currency", currency: c, notation: n >= 100000 ? "compact" : "standard", maximumFractionDigits: n >= 100000 ? 1 : 0 }); }
  catch { return `${c} ${Math.round(n)}`; }
};

export function Fee({ min, max, suffix = "", plus }) {
  const { currency, rate } = useCurrency();
  const range = (f) => `${f(min)}${max ? "–" + f(max) : ""}${plus ? "+" : ""}${suffix}`;
  return (
    <span data-testid="fee-usd">
      <span className="font-mono text-white">{range(usd)}</span>
      {currency !== "USD" && <span className="block font-mono text-[11px] text-slate-400">≈ {range((v) => compact(v * rate, currency))}</span>}
    </span>
  );
}
